import type { ChatMessage, ChatPart, UiMessageDto } from "../types/chat";

/**
 * Pure helpers for the chat SSE transport: framing the raw byte stream into
 * `UiMessageDto` events, and reducing a turn's parts as deltas arrive.
 *
 * Kept free of Vue/Nuxt so the merge rules can be unit-tested in isolation
 * (B10). The stateful transport (`useChat`) composes these.
 */

/**
 * Splits a growing SSE buffer into complete frames. Returns the parsed events
 * and the unconsumed remainder of the buffer (a partial frame).
 *
 * Handles both `\n\n` and `\r\n\r\n` frame separators, and frames that carry
 * multiple `data:` lines (joined with `\n` per the SSE spec).
 */
export function drainSseFrames(buffer: string): {
  events: UiMessageDto[];
  rest: string;
} {
  const normalized = buffer.replace(/\r\n/g, "\n");
  const events: UiMessageDto[] = [];
  let rest = normalized;
  let separator: number;

  while ((separator = rest.indexOf("\n\n")) !== -1) {
    const frame = rest.slice(0, separator);
    rest = rest.slice(separator + 2);
    const event = parseSseFrame(frame);
    if (event) {
      events.push(event);
    }
  }

  return { events, rest };
}

/**
 * Parses a single SSE frame (one event) into a `UiMessageDto`. Returns `null`
 * for comment-only / empty / unparseable frames so callers can skip them.
 */
export function parseSseFrame(frame: string): UiMessageDto | null {
  const dataLines = frame
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).replace(/^ /, ""));

  if (dataLines.length === 0) {
    return null;
  }

  const json = dataLines.join("\n").trim();
  if (!json) {
    return null;
  }

  try {
    return JSON.parse(json) as UiMessageDto;
  } catch {
    return null;
  }
}

/** Stable key for a part within a turn: invocationId for tools, else the backend part id. */
function partKey(dto: UiMessageDto): string {
  if (dto.type === "tool" && dto.tool) {
    return `tool:${dto.tool.invocationId}`;
  }
  return `${dto.type}:${dto.id}`;
}

/**
 * Applies a single streamed `UiMessageDto` to the parts of the in-flight
 * assistant turn, mutating and returning a NEW parts array (so Vue list-level
 * consumers re-render).
 *
 * Rules:
 *  - message / reasoning: append non-empty `content` deltas; set `isStreaming`.
 *  - tool: upsert by invocationId, replacing input/output/state.
 *  - error: set the error text; not streaming.
 * Empty-content finalization parts (`isStreaming: false`, no content) only flip
 * the streaming flag — they never duplicate text.
 */
export function applyTurnDelta(parts: ChatPart[], dto: UiMessageDto): ChatPart[] {
  // `complete` is a terminal, transient signal handled by the transport (host navigation), not a
  // render part — never merge it into the turn's parts.
  if (dto.type === "complete") {
    return parts;
  }

  const key = partKey(dto);
  const index = parts.findIndex((p) => p.key === key);
  const next = parts.slice();

  if (index === -1) {
    next.push(createPart(key, dto));
    return next;
  }

  const existing = next[index]!;
  next[index] = mergePart(existing, dto);
  return next;
}

function createPart(key: string, dto: UiMessageDto): ChatPart {
  return {
    key,
    type: dto.type,
    text: dto.type === "error" ? dto.content ?? "" : dto.content ?? "",
    isStreaming: dto.isStreaming,
    tool: dto.type === "tool" && dto.tool ? dto.tool : undefined,
    error: dto.type === "error" && dto.error ? dto.error : undefined,
    sequence: dto.sequence,
  };
}

function mergePart(existing: ChatPart, dto: UiMessageDto): ChatPart {
  if (dto.type === "tool") {
    return {
      ...existing,
      isStreaming: dto.isStreaming,
      tool: dto.tool ?? existing.tool,
    };
  }

  if (dto.type === "error") {
    return {
      ...existing,
      text: dto.content ?? existing.text,
      error: dto.error ?? existing.error,
      isStreaming: false,
    };
  }

  // message / reasoning: append non-empty deltas, always update streaming flag.
  const text = dto.content ? existing.text + dto.content : existing.text;
  return { ...existing, text, isStreaming: dto.isStreaming };
}

/** Marks every still-streaming part of a turn as complete (used on stream close/abort). */
export function finalizeParts(parts: ChatPart[]): ChatPart[] {
  return parts.map((p) => (p.isStreaming ? { ...p, isStreaming: false } : p));
}

/** Convenience factory for a client-side chat turn. */
export function createMessage(
  id: string,
  role: ChatMessage["role"],
  parts: ChatPart[] = [],
): ChatMessage {
  return { id, role, parts, createdAt: Date.now() };
}
